/**
 * Event message board: comments (polled every 25 s while visible), composer at the bottom with a
 * client UUID per message, reply quotes, edit/delete own (editors may delete any), and automatic
 * "mark as read" when the board is on screen.
 */
import { useQueryClient } from '@tanstack/react-query';
import { MessageCircle, SendHorizontal, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type RefObject } from 'react';
import type { EventDTO } from '../../../shared/dto/events';
import { uuid } from '../../../shared/ids';
import {
  discardLocalComment,
  markCommentsRead,
  submitComment,
  useComments,
  useDeleteComment,
  useEditComment,
  type BoardComment,
} from '../../api/calendar';
import { useRequiredMe } from '../../auth/AuthProvider';
import { useToast } from '../../components/toast';
import { Button, Card, ConfirmSheet, ErrorBox, IconButton, Spinner } from '../../components/ui';
import { useT } from '../../i18n';
import { COMMENT_MAX, CommentItem } from './CommentItem';
import { errorText, useIsEditor } from './shared';

function Composer({
  replyTo,
  onCancelReply,
  onSend,
}: {
  replyTo: BoardComment | null;
  onCancelReply: () => void;
  onSend: (body: string) => void;
}) {
  const t = useT();
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, [text]);

  useEffect(() => {
    if (replyTo) ref.current?.focus();
  }, [replyTo]);

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const body = text.trim();
    if (!body) return;
    onSend(body);
    setText('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
  };

  return (
    <form
      onSubmit={submit}
      className="sticky bottom-0 z-10 -mx-5 flex flex-col gap-2 border-t border-[var(--line)] bg-[var(--surface)] px-5 pt-3 pb-3 md:-mx-6 md:px-6"
    >
      {replyTo ? (
        <div className="flex items-center gap-2 rounded-xl bg-[var(--accent-soft)] py-1 pr-1 pl-3 text-[15px] text-[var(--accent)]">
          <span className="min-w-0 flex-1 truncate font-semibold">{t('calendar.board.replying_to', { name: replyTo.author.fullName })}</span>
          <IconButton icon={X} label={t('calendar.board.cancel_reply')} onClick={onCancelReply} />
        </div>
      ) : null}
      <div className="flex items-end gap-2">
        <textarea
          ref={ref}
          rows={1}
          value={text}
          maxLength={COMMENT_MAX}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('calendar.board.placeholder')}
          aria-label={t('calendar.board.placeholder')}
          className="max-h-60 min-h-12 flex-1 resize-none rounded-2xl border border-[var(--line)] bg-[var(--surface)] px-4 py-2.5 text-[17px] text-[var(--ink)] placeholder:text-[var(--muted)] focus:border-[var(--focus)] focus:ring-2 focus:ring-[var(--focus)]/30 focus:outline-none"
        />
        <Button type="submit" icon={SendHorizontal} disabled={!text.trim()} className="min-h-12 shrink-0">
          <span className="hidden sm:inline">{t('calendar.board.send')}</span>
          <span className="sr-only sm:hidden">{t('calendar.board.send')}</span>
        </Button>
      </div>
    </form>
  );
}

/** Marks the board read when it is on screen and the page is visible, once per batch of new messages from others. */
function useAutoMarkRead(ev: EventDTO, comments: BoardComment[] | undefined, meId: string, target: RefObject<HTMLElement | null>) {
  const qc = useQueryClient();
  const latestOther = useMemo(() => {
    let max = 0;
    for (const c of comments ?? []) if (!c.local && c.author.id !== meId && c.createdAt > max) max = c.createdAt;
    return max;
  }, [comments, meId]);
  const baseline = useRef<number | null>(null);
  if (baseline.current === null && comments) baseline.current = ev.unreadCount === 0 ? latestOther : 0;

  useEffect(() => {
    const el = target.current;
    if (!el || !comments) return;
    const needs = ev.unreadCount > 0 || latestOther > (baseline.current ?? 0);
    if (!needs) return;
    let inView = false;
    let done = false;
    const tryMark = () => {
      if (done || !inView || document.visibilityState !== 'visible') return;
      done = true;
      baseline.current = Math.max(baseline.current ?? 0, latestOther);
      void markCommentsRead(ev.id, qc);
    };
    const io = new IntersectionObserver(
      (entries) => {
        inView = entries.some((e) => e.isIntersecting);
        tryMark();
      },
      { threshold: 0 },
    );
    io.observe(el);
    document.addEventListener('visibilitychange', tryMark);
    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', tryMark);
    };
  }, [ev.id, ev.unreadCount, latestOther, comments, qc, target]);
}

export function CommentBoard({ ev, now }: { ev: EventDTO; now: number }) {
  const t = useT();
  const me = useRequiredMe();
  const editor = useIsEditor();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useComments(ev.id);
  const edit = useEditComment(ev.id);
  const del = useDeleteComment(ev.id);
  const [replyTo, setReplyTo] = useState<BoardComment | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<BoardComment | null>(null);
  const boardRef = useRef<HTMLElement>(null);

  useAutoMarkRead(ev, q.data, me.id, boardRef);

  // Messages that were unread when the board opened (or arrived while it is open) get a "Ný" marker.
  const [openedAt] = useState(now);
  const initialUnread = useRef<number | null>(null);
  if (initialUnread.current === null && q.data) initialUnread.current = ev.unreadCount;
  const newIds = useMemo(() => {
    const ids = new Set<string>();
    const others = (q.data ?? []).filter((c) => !c.local && c.author.id !== me.id && c.body !== null);
    const n = initialUnread.current ?? 0;
    for (const c of others.slice(Math.max(0, others.length - n))) ids.add(c.id);
    for (const c of others) if (c.createdAt > openedAt) ids.add(c.id);
    return ids;
  }, [q.data, me.id, openedAt]);

  const list = q.data ?? [];
  const byId = useMemo(() => new Map(list.map((c) => [c.id, c])), [list]);
  const repliedTo = useMemo(() => new Set(list.map((c) => c.replyTo).filter((x): x is string => !!x)), [list]);
  // Deleted messages are hidden unless someone replied to them (then a muted placeholder keeps the thread readable).
  const visible = list.filter((c) => c.body !== null || repliedTo.has(c.id));

  const author = {
    id: me.id,
    fullName: me.fullName,
    section: me.section,
    title: me.title,
    avatarRev: me.avatarRev,
    inactive: false,
  };

  const send = async (body: string, reply: string | null, id = uuid()) => {
    try {
      const r = await submitComment(ev.id, { id, body, replyTo: reply }, author, qc);
      if (r === 'queued') toast.show(t('calendar.saved_offline'));
    } catch (e) {
      toast.show(errorText(t, e), { tone: 'error' });
    }
  };

  const onEdit = async (id: string, body: string) => {
    try {
      await edit.mutateAsync({ id, body });
      return true;
    } catch (e) {
      toast.show(errorText(t, e), { tone: 'error' });
      return false;
    }
  };

  return (
    <Card as="section" className="flex flex-col p-5 md:p-6">
      <section ref={boardRef} id="spjall" className="scroll-mt-24" aria-label={t('calendar.board.title')}>
        <h2 className="flex items-center gap-2 text-[20px] font-bold">
          <MessageCircle className="size-6 text-[var(--accent)]" aria-hidden />
          {t('calendar.board.title')}
          {q.isFetching && q.data ? <Spinner className="[&_svg]:size-4" /> : null}
        </h2>

        {q.isPending && q.fetchStatus === 'paused' ? (
          <p className="py-8 text-center text-[var(--muted)]">{t('common.offline')}</p>
        ) : q.isPending ? (
          <div className="py-8 text-center">
            <Spinner label={t('common.loading')} />
          </div>
        ) : q.isError && !q.data ? (
          <div className="py-4">
            <ErrorBox onRetry={() => void q.refetch()} retryLabel={t('common.retry')}>
              {t('calendar.board.load_error')}
            </ErrorBox>
          </div>
        ) : visible.length === 0 ? (
          <p className="py-8 text-center text-[var(--muted)]">{t('calendar.board.empty')}</p>
        ) : (
          <ul className="flex flex-col divide-y divide-[var(--line)]">
            {visible.map((c) => (
              <CommentItem
                key={c.id}
                c={c}
                parent={c.replyTo ? (byId.get(c.replyTo) ?? null) : null}
                mine={c.author.id === me.id}
                canDelete={editor}
                now={now}
                isNew={newIds.has(c.id)}
                onReply={(x) => setReplyTo(x)}
                onEdit={onEdit}
                onDelete={(x) => setConfirmDelete(x)}
                onRetry={(x) => void send(x.body ?? '', x.replyTo, x.id)}
                onDiscard={(x) => discardLocalComment(ev.id, x.id, qc)}
              />
            ))}
          </ul>
        )}
      </section>

      <Composer
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        onSend={(body) => {
          const reply = replyTo?.id ?? null;
          setReplyTo(null);
          void send(body, reply);
        }}
      />

      <ConfirmSheet
        open={!!confirmDelete}
        title={t('calendar.board.delete_title')}
        body={t('calendar.board.delete_body')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        danger
        busy={del.isPending}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => {
          const target = confirmDelete;
          if (!target) return;
          del.mutate(target.id, {
            onSuccess: () => setConfirmDelete(null),
            onError: (e) => {
              setConfirmDelete(null);
              toast.show(errorText(t, e), { tone: 'error' });
            },
          });
        }}
      />
    </Card>
  );
}
